import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { ExpandedTooltip, Icon, toast } from '@livable/saas-react-components';
import { Button } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useMemo, useState } from 'react';
import { buildSuggestionItems, getSuggestionKey, getTableUtilityLabel, hasOverlappingBills, isAwaitingSpikeReview, useAllocatedTables, useAllocationComparisons, useAllocationSuggestions, usePropertyGoodwillDeductions } from 'src/components/allocations/AllocationSuggestion';
import { isOldServicePeriod, OldServicePeriodWarning } from 'src/components/allocations/OldServicePeriod';
import { useStore } from 'src/stores';
import { buildAllocationTables, buildApproveItems, hasAllocationTable, indexAllocationStates, isApprovable, isAwaitingApproval, isHeldForReview, isPendingAllocation, withAllocations } from './allocationTables';
import useStyles from './billDetailsStyles';
import { withAllocationSetting } from './billEditUtils';
import { matchBillUtilities, selectServices } from './billUtilityMatching';
import { ConfirmActionModal } from './ConfirmActionModal';
import { PropertyAllocation } from './PropertyAllocation';
import { useBillAllocations } from './useBillAllocations';
import { useChargeOnDates } from './useChargeOnDates';

/**
 * Allocating only works the tables out and stops at "Awaiting approval" (LB-6052), so it still asks before
 * allocating everything at once but no longer says anybody is charged.
 */
const ALLOCATE_ALL_CONFIRMATION = {
  confirmLabel: 'Allocate all',
  message: 'This will allocate every pending service on this bill across the listed properties and utilities, and work ' + 'out what each resident would be charged. Nothing is charged until each table is approved.',
  title: 'Allocate all services?'
};
const ALLOCATE_ALL_HINT = 'Creates the allocation for each pending service and works out its table. Nothing is charged until the table is ' + 'approved.';

/** Approving is what charges residents, so it is what asks to have the charges reviewed. */
const APPROVE_REVIEWED = 'Please confirm that the amounts and resident charges have been reviewed.';
const APPROVE_ALL_HINT = 'Approves every table awaiting approval and charges the residents shown.';

// A charge on date picked on a table couldn't be saved, so its charges would go to AppFolio on a date nobody chose.
const NOT_APPROVED_UNSAVED_CHARGE_ON = "Nothing was approved: a charge on date couldn't be saved. Please try again.";

/** A table's service period as the long-past warning names it: its utility and, where known, its property. */
const toServicePeriod = (table, address, dates) => _objectSpread(_objectSpread({}, dates), {}, {
  label: [getTableUtilityLabel(table), address].filter(Boolean).join(' at ')
});

/** The dates a table will be allocated on: the bill's own, as the table shows them. */
const suggestedDatesOf = table => ({
  endDateOnly: table.serviceEndDate,
  startDateOnly: table.serviceStartDate
});

/** The dates an approval charges: the saved allocation's, which Apply Billing may have moved off the bill's own. */
const approvedDatesOf = table => ({
  endDateOnly: table.savedServiceEndDate,
  startDateOnly: table.savedServiceStartDate
});
const REVIEW_LEFT_OUT_HINT = 'A service that rose sharply on the last bill is left out until the comparison shown against it has been reviewed.';

/**
 * Allocation tables for every property/utility on the bill (Figma: "Allocation suggestions" card). The
 * charges come from the allocation service, which calculates them without saving anything, so editing
 * an input asks it again and the tables follow.
 */
export const AllocationSuggestionsCard = observer(_ref => {
  var _store$company;
  let billId = _ref.billId,
    currency = _ref.currency,
    data = _ref.data,
    _ref$monthInProgress = _ref.monthInProgress,
    monthInProgress = _ref$monthInProgress === void 0 ? null : _ref$monthInProgress,
    onSaveSettings = _ref.onSaveSettings,
    properties = _ref.properties,
    propertyIds = _ref.propertyIds,
    settings = _ref.settings;
  const classes = useStyles();
  const store = useStore();
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    draftSettings = _useState2[0],
    setDraftSettings = _useState2[1];
  const _useState3 = useState({}),
    _useState4 = _slicedToArray(_useState3, 2),
    targetOverrides = _useState4[0],
    setTargetOverrides = _useState4[1];
  // The confirmation on screen, kept as it was while it closes so its words do not change on the way out.
  const _useState5 = useState(_objectSpread(_objectSpread({}, ALLOCATE_ALL_CONFIRMATION), {}, {
      isOpen: false
    })),
    _useState6 = _slicedToArray(_useState5, 2),
    confirmation = _useState6[0],
    setConfirmation = _useState6[1];
  const isEditing = draftSettings !== null;
  const activeSettings = isEditing ? draftSettings : settings;

  // Only what the property is set up for is allocated; the rest of the bill is shown above.
  const services = useMemo(() => selectServices(matchBillUtilities({
    billPropertyIds: propertyIds,
    data,
    properties,
    targetOverrides
  }), true), [data, properties, propertyIds, targetOverrides]);
  const goodwillDeductions = usePropertyGoodwillDeductions(services.flatMap(group => group.services.map(_ref2 => {
    let target = _ref2.target;
    return target.propertyId;
  })));
  const groups = useMemo(() => buildAllocationTables({
    data,
    goodwillDeductions,
    groups: services,
    settings: activeSettings
  }), [activeSettings, data, goodwillDeductions, services]);
  const _useBillAllocations = useBillAllocations(billId),
    allocate = _useBillAllocations.allocate,
    approve = _useBillAllocations.approve,
    allocationError = _useBillAllocations.error,
    isBusy = _useBillAllocations.isBusy,
    areStatesRead = _useBillAllocations.isRead,
    allocationServices = _useBillAllocations.services;
  const allocationStates = useMemo(() => indexAllocationStates(allocationServices), [allocationServices]);
  // What was actually allocated, for the services far enough along to have a table of their own.
  const allocatedTables = useAllocatedTables(allocationServices.filter(hasAllocationTable));
  // A service held back for review cannot be approved until its comparison has been looked at.
  const _useAllocationCompari = useAllocationComparisons(allocationServices.filter(isHeldForReview)),
    comparisons = _useAllocationCompari.comparisons,
    review = _useAllocationCompari.review,
    reviewingKeys = _useAllocationCompari.savingKeys;

  // Nothing is suggested for a service that is already allocated: its table is the real one.
  const items = useMemo(() => buildSuggestionItems(groups.flatMap(group => group.utilities).filter(table => !allocatedTables["".concat(table.propertyId, ":").concat(table.utilityId)])), [allocatedTables, groups]);
  const _useAllocationSuggest = useAllocationSuggestions(items),
    hasFailed = _useAllocationSuggest.hasFailed,
    isLoading = _useAllocationSuggest.isLoading,
    suggestions = _useAllocationSuggest.suggestions;
  const allocations = withAllocations(groups, {
    allocatedTables,
    allocationStates,
    areStatesRead,
    comparisons,
    // Read here, inside the observer, so late-arriving resident names re-render the tables.
    getResidentName: accountId => store.account.profilesFullName.get(accountId),
    hasFailed,
    isLoading,
    monthInProgress,
    reviewingKeys,
    suggestions
  });
  const tables = allocations.flatMap(allocation => allocation.utilities || []);
  // The AppFolio charge on date of each table, for an AppFolio company (LB-6118, requested by Haley).
  const chargeOnDates = useChargeOnDates({
    allocatedTables,
    allocationStates,
    enabled: !!((_store$company = store.company) !== null && _store$company !== void 0 && (_store$company = _store$company.companyFeatureStore) !== null && _store$company !== void 0 && _store$company.isAppfolio),
    isBusy,
    monthInProgress,
    tables
  });
  // Busy, or an approval waiting on a charge on date to be saved: either way nothing else is sent meanwhile, or the
  // same tables could be approved twice.
  const isHeld = isBusy || chargeOnDates.isSettling;
  // Nor before the bill's allocation states are in: until then a table awaiting approval reads as one nobody has
  // allocated, and its own allocation as a bill it would charge twice (LB-6052).
  const canSend = !!billId && !isHeld && areStatesRead;
  const approvableTables = tables.filter(isApprovable);
  const pendingTables = approvableTables.filter(isPendingAllocation);
  // Worked out and waiting for somebody to approve them, which is what charges the residents (LB-6052).
  const awaitingTables = tables.filter(isAwaitingApproval);
  // What "Approve all" sends, one per property utility; counted from this, not from the rows on screen.
  const awaitingApprovals = buildApproveItems(awaitingTables);
  // Held back by the allocation service until the rise on the last bill has been compared.
  const heldTables = tables.filter(isAwaitingSpikeReview);
  // One utility billed twice over is enough to hold the whole bill back: allocating the rest in bulk
  // would quietly leave a bill that cannot be saved as it stands.
  const billedTables = tables.filter(hasOverlappingBills);
  if (!tables.length) return null;
  const propertyOptions = properties.map(property => ({
    id: property.propertyId,
    label: property.propertyAddress || property.propertyName || property.propertyId
  }));
  const handleChangeSetting = (propertyIndex, serviceType, patch) => setDraftSettings(current => withAllocationSetting(current, propertyIndex, serviceType, patch));
  const handleSelectTarget = (key, patch) => setTargetOverrides(current => _objectSpread(_objectSpread({}, current), {}, {
    [key]: _objectSpread(_objectSpread({}, current[key] || {}), patch)
  }));
  const handleSave = () => {
    onSaveSettings(draftSettings);
    setDraftSettings(null);
  };

  // Recorded against the cycle the service was held in, which is where the comparison was read.
  const handleReview = key => {
    const table = tables.find(item => item.key === key);
    const allocation = allocationStates[getSuggestionKey(table === null || table === void 0 ? void 0 : table.propertyId, table === null || table === void 0 ? void 0 : table.utilityId)];
    if (allocation !== null && allocation !== void 0 && allocation.billingCycleId) {
      review({
        billingCycleId: allocation.billingCycleId,
        propertyId: allocation.propertyId,
        utilityId: allocation.utilityId
      });
    }
  };
  const addressOf = table => {
    var _allocations$find;
    return (_allocations$find = allocations.find(allocation => (allocation.utilities || []).includes(table))) === null || _allocations$find === void 0 ? void 0 : _allocations$find.address;
  };

  // The tables among these whose service period ended more than three months ago, as the warning names them
  // (LB-6030). Allocating one asks first and records who said yes; approving one warns again.
  const oldPeriodsOf = function (list) {
    let datesOf = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : suggestedDatesOf;
    return list.filter(table => isOldServicePeriod(datesOf(table).endDateOnly)).map(table => toServicePeriod(table, addressOf(table), datesOf(table)));
  };
  const withOldPeriodWarning = function (message, periods) {
    let unchecked = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
    return periods.length || unchecked.length ? /*#__PURE__*/React.createElement(React.Fragment, null, message, /*#__PURE__*/React.createElement(OldServicePeriodWarning, {
      periods: periods,
      sx: {
        mt: 2
      },
      unchecked: unchecked
    })) : message;
  };

  // What an approval charges is the saved allocation. Until it has been read its period is unknown, and approving
  // then says so rather than judge the bill's own dates, which Apply Billing may have moved.
  const oldApprovalPeriodsOf = list => oldPeriodsOf(list.filter(table => table.savedServiceEndDate), approvedDatesOf);
  const uncheckedOf = list => list.filter(table => !table.savedServiceEndDate).map(table => toServicePeriod(table, addressOf(table), {}).label);

  // Allocated on the values the table was suggested on: what was shown is what gets charged. A table for a
  // long-past period asks first; any other allocates straight away, as it always has.
  const handleAllocate = key => {
    const chosen = tables.filter(table => table.key === key);
    const oldPeriods = oldPeriodsOf(chosen);
    if (!oldPeriods.length) {
      allocate(buildSuggestionItems(chosen));
      return;
    }
    setConfirmation({
      allocateTables: chosen,
      confirmLabel: 'Allocate',
      confirmsOldServicePeriod: true,
      isOpen: true,
      message: /*#__PURE__*/React.createElement(OldServicePeriodWarning, {
        periods: oldPeriods
      }),
      title: 'Allocate a bill for a long-past period?'
    });
  };
  // The tables are named when the button is pressed, so what is allocated is what the warning listed.
  const handleAllocateAll = () => {
    const oldPeriods = oldPeriodsOf(pendingTables);
    setConfirmation(_objectSpread(_objectSpread({}, ALLOCATE_ALL_CONFIRMATION), {}, {
      allocateTables: pendingTables,
      confirmsOldServicePeriod: oldPeriods.length > 0,
      isOpen: true,
      message: withOldPeriodWarning(ALLOCATE_ALL_CONFIRMATION.message, oldPeriods)
    }));
  };

  // The tables are named when the button is pressed: a table that reaches "Awaiting approval" while the
  // confirmation is open was never put to the person, so it is not approved on their behalf.
  const handleApprove = key => {
    const table = tables.find(item => item.key === key);
    if (!table) return;
    const address = addressOf(table);
    setConfirmation({
      approveTables: buildApproveItems([table]),
      confirmLabel: 'Approve',
      isOpen: true,
      message: withOldPeriodWarning("This will approve the ".concat(getTableUtilityLabel(table), " allocation").concat(address ? " for ".concat(address) : '', " and charge the residents shown. ").concat(APPROVE_REVIEWED), oldApprovalPeriodsOf([table]), uncheckedOf([table])),
      title: 'Approve this allocation?'
    });
  };
  const handleApproveAll = () => setConfirmation({
    approveTables: awaitingApprovals,
    confirmLabel: 'Approve all',
    isOpen: true,
    message: withOldPeriodWarning("This will approve the ".concat(awaitingApprovals.length, " allocations awaiting approval on this bill and charge the residents shown. ").concat(APPROVE_REVIEWED), oldApprovalPeriodsOf(awaitingTables), uncheckedOf(awaitingTables)),
    title: 'Approve all allocations?'
  });
  const handleConfirm = () => {
    // The dialog fades out over a few hundred milliseconds and its button still takes a click meanwhile: a second
    // one would send the same approval again. Only the click that closes an open dialog counts.
    if (!confirmation.isOpen || !canSend) return;
    setConfirmation(current => _objectSpread(_objectSpread({}, current), {}, {
      isOpen: false
    }));
    if (confirmation.approveTables) {
      // The charges go to AppFolio with the date they have when approved: any date picked is saved first.
      const approving = confirmation.approveTables;
      const keys = approving.map(item => getSuggestionKey(item.propertyId, item.utilityId));
      if (!chargeOnDates.hasUnsaved(keys)) {
        approve(approving);
        return;
      }
      chargeOnDates.settle(keys).then(isSettled => {
        if (isSettled) approve(approving);else toast('error', {
          body: [NOT_APPROVED_UNSAVED_CHARGE_ON]
        });
      });
      return;
    }
    const allocating = buildSuggestionItems(confirmation.allocateTables);
    if (confirmation.confirmsOldServicePeriod) allocate(allocating, {
      confirmedOldServicePeriod: true
    });else allocate(allocating);
  };
  const allocatedCount = approvableTables.filter(table => table.isApproved).length;

  /**
   * An approved table has already charged residents; the settings behind it no longer decide anything.
   * `isHeld` closes the same door while an allocation is on its way, or an approval waits on a charge on date,
   * where an edit lands after the request that would have carried it and is lost without a trace.
   */
  const editBlockedReason = (() => {
    // Until the states are in, an approved table reads as one nobody has allocated yet.
    if (!areStatesRead) return "Checking where this bill's allocations stand.";
    if (isHeld) {
      return 'This bill is being allocated or approved. The allocations can be edited again if it does not go through.';
    }
    if (allocatedCount) return 'These allocations have been approved and can no longer be edited.';
    return null;
  })();

  // Once something has been allocated the card is no longer only a suggestion.
  const title = (() => {
    if (isEditing) return 'Edit allocation suggestions';
    return allocatedCount ? 'Allocations' : 'Allocation suggestions';
  })();
  const status = (() => {
    if (billedTables.length) return {
      className: classes.statusChipWarning,
      label: 'Service dates already billed'
    };
    if (!approvableTables.length) {
      return {
        className: '',
        label: isLoading || !areStatesRead ? 'Calculating' : 'Nothing to allocate'
      };
    }
    if (allocatedCount === approvableTables.length) return {
      className: classes.statusChipDone,
      label: 'Allocated'
    };
    return {
      className: '',
      label: "".concat(allocatedCount, "/").concat(approvableTables.length, " allocated")
    };
  })();
  const allocateAllHint = billedTables.length ? 'Some service dates on this bill are already billed, so nothing can be allocated until they change.' : ALLOCATE_ALL_HINT;
  const approveAllHint = heldTables.length ? "".concat(APPROVE_ALL_HINT, " ").concat(REVIEW_LEFT_OUT_HINT) : APPROVE_ALL_HINT;

  // Each bulk button stands for two or more tables it would act on: with one, that table's own button is the only one,
  // and "Allocate all" beside it would offer the same click twice (LB-6036).
  const offersAllocateAll = pendingTables.length > 1;
  const offersApproveAll = awaitingApprovals.length > 1;
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.card, " ").concat(classes.cardTinted, " ").concat(classes.cardClipped)
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeader
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeaderTitles
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.cardTitle
  }, title), isEditing ? null : /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.statusChip, " ").concat(status.className)
  }, status.label)), isEditing ? null :
  /*#__PURE__*/
  // Once a table is approved its charges are on residents' ledgers, and editing the settings
  // behind it changes nothing that anyone will read. `isBusy` covers the same window while an
  // allocation is still in flight, where an edit is discarded without trace.
  React.createElement(ExpandedTooltip, {
    placement: "top",
    title: editBlockedReason !== null && editBlockedReason !== void 0 ? editBlockedReason : '',
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("button", {
    "aria-label": "Edit allocations",
    className: classes.iconButton,
    disabled: !onSaveSettings || !!editBlockedReason,
    onClick: () => setDraftSettings(settings || {}),
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "edit"
  }))))), allocationError ? /*#__PURE__*/React.createElement("div", {
    className: classes.summary
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.matchBox, " ").concat(classes.matchBoxWarning)
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "warning",
    type: "warning"
  }), /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.matchText, " ").concat(classes.matchTextWarning)
  }, allocationError))) : null, allocations.map((allocation, index) => /*#__PURE__*/React.createElement(PropertyAllocation, {
    allocation: allocation,
    chargeOnFor: isEditing ? undefined : chargeOnDates.chargeOnFor,
    currency: currency,
    defaultExpanded: index === 0,
    isEditing: isEditing,
    key: allocation.address || index,
    onAllocate: canSend ? handleAllocate : undefined,
    onApprove: canSend ? handleApprove : undefined,
    onChangeSetting: handleChangeSetting,
    onReview: handleReview,
    onSelectTarget: handleSelectTarget,
    propertyOptions: propertyOptions
  })), isEditing ? /*#__PURE__*/React.createElement("div", {
    className: classes.editFooter
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => setDraftSettings(null),
    size: "small",
    variant: "outlined"
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    onClick: handleSave,
    size: "small",
    variant: "contained"
  }, "Save")) : null, !isEditing && (offersAllocateAll || offersApproveAll) ? /*#__PURE__*/React.createElement("div", {
    className: classes.cardActions
  }, offersAllocateAll ? /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: allocateAllHint,
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
    disabled: !canSend || !!billedTables.length,
    onClick: handleAllocateAll,
    size: "small",
    variant: "contained"
  }, "Allocate all"))) : null, offersApproveAll ? /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: approveAllHint,
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
    disabled: !canSend,
    onClick: handleApproveAll,
    size: "small",
    variant: "contained"
  }, "Approve all"))) : null) : null, /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: confirmation.confirmLabel,
    isOpen: confirmation.isOpen,
    message: confirmation.message,
    onClose: () => setConfirmation(current => _objectSpread(_objectSpread({}, current), {}, {
      isOpen: false
    })),
    onConfirm: handleConfirm,
    title: confirmation.title
  }));
});